<template>
  <v-card variant="outlined" class="telegram-digest-card mt-4">
    <div class="d-flex align-center ga-3 pa-3 flex-wrap">
      <v-icon size="20">mdi-bell-ring-outline</v-icon>
      <div>
        <div class="text-subtitle-2 font-weight-medium">
          {{ $t("ncrm.integrations.telegram.digest.title") }}
        </div>
        <div class="text-caption text-medium-emphasis">
          {{ $t("ncrm.integrations.telegram.digest.subtitle") }}
        </div>
      </div>
      <v-spacer />
      <v-chip
        :color="account.digest_enabled ? 'success' : 'neutral'"
      >
        {{
          $t(
            "ncrm.integrations.telegram.digest.status." +
              (account.digest_enabled ? "active" : "disabled")
          )
        }}
      </v-chip>
    </div>
    <v-divider />
    <div class="pa-3">
      <div class="d-flex flex-wrap align-end ga-3 mb-3">
        <v-switch
          v-model="form.enabled"
          :label="$t('ncrm.integrations.telegram.digest.fields.enabled')"
          color="primary"
          density="comfortable"
          hide-details
          inset
          @update:model-value="onAnyChange"
        />
        <v-text-field
          v-model="form.time"
          :label="$t('ncrm.integrations.telegram.digest.fields.time')"
          type="time"
          hide-details
          style="max-width: 160px"
          @update:model-value="onAnyChange"
        />
        <v-select
          v-model="form.timezone"
          :items="timezoneOptions"
          :label="$t('ncrm.integrations.telegram.digest.fields.timezone')"
          hide-details
          style="max-width: 240px"
          @update:model-value="onAnyChange"
        />
        <v-spacer />
        <v-btn
          color="primary"
          variant="tonal"
          size="small"
          :loading="busy === 'save'"
          :disabled="!dirty"
          @click="save"
        >
          <v-icon start size="16">mdi-content-save-outline</v-icon>
          {{ $t("ncrm.integrations.telegram.digest.actions.save") }}
        </v-btn>
        <v-btn
          variant="text"
          size="small"
          :loading="busy === 'run'"
          @click="runNow"
        >
          <v-icon start size="16">mdi-send</v-icon>
          {{ $t("ncrm.integrations.telegram.digest.actions.runNow") }}
        </v-btn>
      </div>

      <div class="text-caption text-medium-emphasis">
        <span v-if="account.digest_last_sent_date">
          {{
            $t("ncrm.integrations.telegram.digest.lastSent", {
              date: formatDate(account.digest_last_sent_date),
            })
          }}
        </span>
        <span v-else>
          {{ $t("ncrm.integrations.telegram.digest.neverSent") }}
        </span>
      </div>

      <v-alert
        v-if="runSummary"
        type="info"
        density="compact"
        class="mt-3"
      >
        {{
          $t("ncrm.integrations.telegram.digest.runSummary", {
            sent: runSummary.sent,
            total: runSummary.total,
            skipped: runSummary.skipped_empty,
            failed: runSummary.failed,
          })
        }}
      </v-alert>
      <v-alert
        v-if="errorMessage"
        type="error"
        density="compact"
        class="mt-3"
      >
        {{ errorMessage }}
      </v-alert>
    </div>
  </v-card>
</template>

<script>
import axios from "axios";
import { formatDisplayDate } from "@/Utile/displayFormat.mjs";

const TIMEZONES = [
  "Europe/Chisinau",
  "Europe/Bucharest",
  "Europe/Kiev",
  "Europe/Moscow",
  "Europe/Berlin",
  "Europe/London",
  "UTC",
];

const normalizeTime = (raw) => {
  // Accepts "HH:MM" or "HH:MM:SS"; UI shows HH:MM.
  if (!raw) return "";
  const m = /^(\d{2}:\d{2})/.exec(String(raw));
  return m ? m[1] : "";
};

export default {
  name: "TelegramDigestCard",
  props: {
    account: { type: Object, required: true },
  },
  emits: ["account-updated", "notify"],
  data() {
    return {
      busy: null,
      errorMessage: "",
      runSummary: null,
      form: {
        enabled: !!this.account.digest_enabled,
        time: normalizeTime(this.account.digest_time) || "09:00",
        timezone: this.account.digest_timezone || "Europe/Chisinau",
      },
      dirty: false,
    };
  },
  computed: {
    timezoneOptions() {
      return TIMEZONES.map((tz) => ({ title: tz, value: tz }));
    },
  },
  watch: {
    account: {
      handler(next) {
        // Re-sync form when parent re-fetches accounts (after save).
        this.form = {
          enabled: !!next.digest_enabled,
          time: normalizeTime(next.digest_time) || "09:00",
          timezone: next.digest_timezone || "Europe/Chisinau",
        };
        this.dirty = false;
      },
      deep: true,
    },
  },
  methods: {
    formatDate(value) {
      if (!value) return "";
      try {
        return formatDisplayDate(value);
      } catch (_) {
        return String(value);
      }
    },
    extractError(error, fallbackKey) {
      return (
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        this.$t(fallbackKey)
      );
    },
    onAnyChange() {
      this.dirty = true;
      this.errorMessage = "";
    },
    async save() {
      this.busy = "save";
      this.errorMessage = "";
      try {
        const { data } = await axios.patch(
          `/integrations/telegram/accounts/${this.account.id}/digest`,
          {
            enabled: this.form.enabled,
            time: this.form.time,
            timezone: this.form.timezone,
          }
        );
        if (data?.account) this.$emit("account-updated", data.account);
        this.dirty = false;
        this.$emit("notify", {
          message: this.$t("ncrm.integrations.telegram.digest.snackbar.saved"),
          color: "success",
        });
      } catch (error) {
        this.errorMessage = this.extractError(
          error,
          "ncrm.integrations.telegram.digest.errors.save"
        );
      } finally {
        this.busy = null;
      }
    },
    async runNow() {
      this.busy = "run";
      this.errorMessage = "";
      this.runSummary = null;
      try {
        const { data } = await axios.post(
          `/integrations/telegram/accounts/${this.account.id}/digest/run-now`,
          {}
        );
        this.runSummary = data;
        this.$emit("notify", {
          message: this.$t("ncrm.integrations.telegram.digest.snackbar.runOk", {
            sent: data?.sent ?? 0,
          }),
          color: "success",
        });
      } catch (error) {
        this.errorMessage = this.extractError(
          error,
          "ncrm.integrations.telegram.digest.errors.run"
        );
      } finally {
        this.busy = null;
      }
    },
  },
};
</script>
